/**
 * =============================================================================
 * WlBillingKpiDetailModal
 *
 * Modal de drill-down dos 4 KPIs principais da aba Cobrança (WL Financeiro):
 *   Receita Total · Total Recebido · A Receber · Vencidos.
 *
 * Abre a partir do click em um `CrmPremiumKpiCard` do `WlBillingKpiCards` e
 * lista de forma paginada e pesquisável todas as parcelas do tenant master
 * que compõem o valor daquele card, para auditoria. Reaproveita a RPC
 * `wl_get_billing_kpis` via `useWlBillingKpiDetails`.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial (drill-down dos 4 KPIs).
 * =============================================================================
 */
import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useWlBillingKpiDetails,
  type WlBillingKpi,
} from '@/hooks/wl/useWlBillingKpis';
import { WlTenantDetailsModal } from '@/components/wl/WlTenantDetailsModal';

interface Props {
  open: boolean;
  onClose: () => void;
  kpi: WlBillingKpi | null;
  label: string | null;
  /** Mês de referência (mesmo aplicado ao card). Null = mês atual. */
  referenceMonth?: string | null;
}

/** Formatador BRL (pt-BR). */
const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(v || 0));

/** Formatador de data pt-BR a partir de ISO date. */
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
};

/** Descrição da regra de negócio de cada KPI (mesma do tooltip do card). */
const KPI_DESC: Record<WlBillingKpi, string> = {
  receita_total:
    'Parcelas cujo VENCIMENTO cai no período selecionado (regime de competência). Exclui cobranças canceladas.',
  total_recebido:
    'Parcelas com status pago cuja DATA DE PAGAMENTO cai no período (regime de caixa).',
  a_receber:
    'Parcelas em aberto (pendente / aguardando) com vencimento a partir de hoje, dentro do período.',
  vencidos:
    'Parcelas com status vencido ou parcelas em aberto cuja data de vencimento já passou (< hoje), com vencimento dentro do período.',
};

/** Classe de badge por status (visual leve). */
const STATUS_BADGE: Record<string, string> = {
  pago: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  vencido: 'bg-red-100 text-red-800 border-red-200',
  pendente: 'bg-amber-100 text-amber-800 border-amber-200',
  aguardando_pagamento: 'bg-blue-100 text-blue-800 border-blue-200',
  aguardando_emissao: 'bg-slate-100 text-slate-700 border-slate-200',
};

export function WlBillingKpiDetailModal({
  open,
  onClose,
  kpi,
  label,
  referenceMonth,
}: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [search, setSearch] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Reset ao trocar de KPI ou reabrir
  useEffect(() => {
    if (open && kpi) {
      setPage(1);
      setSearch('');
    }
  }, [open, kpi]);

  const { data, isLoading, error } = useWlBillingKpiDetails(
    open ? kpi : null,
    page,
    pageSize,
    search,
    referenceMonth,
  );

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  // Soma da página (rodapé informativo).
  const pageSum = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.valor || 0), 0),
    [rows],
  );

  // Total Recebido mostra a coluna Pago em, os demais mostram Dias.
  const showPagoCol = kpi === 'total_recebido';

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Parcelas — {label ?? 'KPI'}</DialogTitle>
            <DialogDescription>
              {kpi ? KPI_DESC[kpi] : ''}
            </DialogDescription>
          </DialogHeader>

          {/* Busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por tenant, cliente ou Nº da fatura..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>

          {/* Tabela */}
          <div className="flex-1 overflow-auto rounded-md border">
            <Table>
              <TableHeader className="sticky top-0 bg-muted/50 z-10">
                <TableRow>
                  <TableHead className="text-xs">Tenant / Cliente</TableHead>
                  <TableHead className="text-xs">Cobrança</TableHead>
                  <TableHead className="text-xs text-center">Parcela</TableHead>
                  <TableHead className="text-xs">Vencimento</TableHead>
                  {showPagoCol ? (
                    <TableHead className="text-xs">Pago em</TableHead>
                  ) : (
                    <TableHead className="text-xs text-center">Dias</TableHead>
                  )}
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {error && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-destructive py-6">
                      Erro ao carregar: {(error as Error).message}
                    </TableCell>
                  </TableRow>
                )}
                {isLoading && !error && (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {!isLoading && !error && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                      Nenhuma parcela encontrada.
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && !error && rows.map((r) => {
                  const tenantDisplay =
                    r.customer_name ??
                    r.tenant_name ??
                    r.tenant_slug ??
                    '—';
                  const statusCls = STATUS_BADGE[r.status] ?? 'bg-muted text-foreground border-muted';
                  return (
                    <TableRow key={r.parcela_id}>
                      <TableCell className="py-2">
                        {r.tenant_id ? (
                          <button
                            type="button"
                            onClick={() => setSelectedTenantId(r.tenant_id)}
                            className="text-left text-sm font-medium text-primary hover:underline"
                          >
                            {tenantDisplay}
                          </button>
                        ) : (
                          <span className="text-sm">{tenantDisplay}</span>
                        )}
                        {r.tenant_slug && r.tenant_slug !== tenantDisplay && (
                          <div className="text-[10px] text-muted-foreground uppercase mt-0.5">
                            {r.tenant_slug}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-2 text-[11px] font-mono text-muted-foreground">
                        {r.numero_fatura ?? r.cobranca_id.slice(0, 8).toUpperCase()}
                      </TableCell>
                      <TableCell className="py-2 text-xs text-center tabular-nums">
                        {r.numero_parcela}
                        {r.total_parcelas ? `/${r.total_parcelas}` : ''}
                      </TableCell>
                      <TableCell className="py-2 text-xs whitespace-nowrap">
                        {fmtDate(r.data_vencimento)}
                      </TableCell>
                      {showPagoCol ? (
                        <TableCell className="py-2 text-xs whitespace-nowrap">
                          {fmtDate(r.data_pagamento)}
                        </TableCell>
                      ) : (
                        <TableCell className="py-2 text-xs text-center tabular-nums">
                          {r.dias_vencido > 0 ? (
                            <span className="text-red-600 font-medium">+{r.dias_vencido}</span>
                          ) : r.dias_vencido === 0 ? (
                            <span className="text-amber-600 font-medium">Hoje</span>
                          ) : (
                            <span className="text-muted-foreground">{r.dias_vencido}</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="py-2">
                        <Badge
                          variant="outline"
                          className={cn('text-[10px] whitespace-nowrap', statusCls)}
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-sm text-right font-medium tabular-nums">
                        {fmtBRL(Number(r.valor))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Rodapé: soma da página + total */}
          <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground pt-1">
            <div>
              {total > 0 && (
                <>
                  Soma da página:{' '}
                  <span className="font-semibold text-foreground">{fmtBRL(pageSum)}</span>
                </>
              )}
            </div>
            <div>Total: {total} parcela{total === 1 ? '' : 's'}</div>
          </div>

          {total > pageSize && (
            <PaginationFooter
              totalItems={total}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(sz) => {
                setPageSize(sz);
                setPage(1);
              }}
              pageSizeOptions={[15, 30, 50]}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Drill-down do tenant selecionado */}
      <WlTenantDetailsModal
        tenantId={selectedTenantId}
        open={!!selectedTenantId}
        onClose={() => setSelectedTenantId(null)}
      />
    </>
  );
}
